import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { useDialog, useLegacyEffect } from "helper/customHook";
import { DIALOG } from "helper/enum/dialogEnum";
import { isOpenDialogSelector } from "redux-store/static/redux/selector";
import { useDispatch, useSelector } from "react-redux";
import pxToRem from "theme/base/pxToRem";
import {
  DialogTitleCustom,
  Title,
} from "modules/PersonnelInformation/Pages/Main/component/modal/style";

import { ReactComponent as IconCalendarEdit } from "assets/svg/20-calendar-edit.svg";
import { ReactComponent as IconClose } from "assets/svg/20-close.svg";

import {
  getRequestLeaveDetailStart,
  resetRequestLeaveDetail,
  setRequestLeaveDetailId,
} from "modules/DeclareLeave/redux/getRequestLeaveDetail";
import {
  selectRequestLeaveDetailData,
  selectRequestLeaveDetailStatus,
} from "modules/DeclareLeave/redux/selector";
import { formatDateStringToDDMMYYYY } from "helper/utils";
import {
  ApproveStatus,
  ApproveStatusValue,
  IApproveStatus,
  LeaveType,
  optionTypeLeave,
  statusColor,
  TitleContent,
} from "../../const";
import { setupDataRequest } from "modules/DeclareLeave/redux/handleRequest";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EmptyState from "components/common/EmptyState";
import { ReduxStateType } from "redux-store/types";
import ListDocument from "../ListDocument";
import { accountInfoDataSelector } from "layouts/Sidenav/redux/selector";
import {
  formReqLeaveReset,
  resetFormLeaveLongTerm,
} from "modules/DeclareLeave/redux/formReqLeave";
import isString from "lodash/isString";
import { useMemo } from "react";
import { declareLeaveURL } from "modules/DeclareLeave/Pages/Main/DeclareLeaveContent";
import {
  getEarlyRegistrationDetailStart,
  setEarlyRegistrationDetailId,
} from "modules/DeclareLeave/redux/getEarlyRegistrationDetail";
import { setDeleteEarlyRegistrationId } from "modules/DeclareLeave/redux/deleteEarlyRegistration";

const LeaveRequestDetailManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const data = useSelector(selectRequestLeaveDetailData);
  const status = useSelector(selectRequestLeaveDetailStatus);
  const { employeeCode } = useSelector(accountInfoDataSelector);

  const { id } = useParams();

  useLegacyEffect(() => {
    if (id && pathname.includes(`/${declareLeaveURL.leave}`)) {
      dispatch(setRequestLeaveDetailId(id));
      dispatch(getRequestLeaveDetailStart());
      requestLeaveDialog.open();
    }
  }, [id, pathname]);

  const requestLeaveDialog = useDialog(DIALOG.VIEW_REQUEST_LEAVE_DETAIL);
  const open = useSelector(
    isOpenDialogSelector(DIALOG.VIEW_REQUEST_LEAVE_DETAIL)
  );
  const approveDialog = useDialog(DIALOG.APPROVE_REQUEST_POPUP);
  const earlyRegistrationDialog = useDialog(DIALOG.EARLY_REGISTRATION_MANAGER);

  const renderBoxContent = (
    title: string,
    content: string | number,
    hasDivider?: boolean,
    paddingBottom: number = 1.5,
    fontWeightContent?: 400 | 500 | 600
  ) => (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      sx={(theme) => ({
        paddingBottom: paddingBottom,
        borderBottom: hasDivider
          ? `1px solid ${theme.palette.stroke.strokeDefault2}`
          : "none",
        marginBottom: hasDivider ? 1.5 : 0,
      })}
    >
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.size.sm_14,
          color: theme.palette.text.textSecondary,
          fontWeight: theme.typography.fw_regular_400,
          width: "40%",
        })}
      >
        {title}
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.size.sm_14,
          color:
            title === TitleContent.status
              ? statusColor(theme, content as IApproveStatus, "color")
              : theme.palette.text.textPrimary,
          fontWeight: fontWeightContent ?? theme.typography.fw_regular_400,
          flex: 1,
          textAlign: "right",
        })}
      >
        {title === TitleContent.status ? ApproveStatus[content] : content}
      </Typography>
    </Box>
  );

  const renderLeaveDetails = () => {
    if (!data?.details || data?.details?.length === 0) return null;
    return (data?.details ?? []).map((leave, index) => {
      const listLeave = data?.details ?? [];
      const date =
        data?.type === LeaveType.SHORT_TERM
          ? `Ngày ${formatDateStringToDDMMYYYY(leave.fromDate)}`
          : `Từ ${formatDateStringToDDMMYYYY(listLeave?.[0]?.fromDate)} đến ${formatDateStringToDDMMYYYY(listLeave?.[listLeave.length - 1]?.toDate)}`;
      if (
        data?.type === LeaveType.SHORT_TERM ||
        (data?.type === LeaveType.LONG_TERM && index === 0)
      ) {
        const hasBorderBottom =
          data?.type === LeaveType.SHORT_TERM && index < listLeave.length - 1;
        return (
          <Box
            key={index}
            sx={(theme) => ({
              display: "flex",
              justifyContent: "space-between",
              paddingY: 1.5,
              borderBottom: hasBorderBottom
                ? `1px solid ${theme.palette.stroke.strokeDefault}`
                : "none",
            })}
          >
            <Box display={"flex"} gap={1.5}>
              <Typography fontSize={14}>{date}</Typography>

              <Typography
                fontSize={14}
                fontWeight={400}
                sx={(theme) => ({
                  color: theme.palette.text.textSecondary,
                  borderLeft: `1px solid ${theme.palette.stroke.strokeDefault}`,
                  paddingLeft: 1.5,
                })}
              >
                {leave?.type === optionTypeLeave.HALF_DAY.value
                  ? "Nghỉ nửa ngày"
                  : "Nghỉ cả ngày"}
              </Typography>
            </Box>
            <Typography
              fontSize={14}
              fontWeight={600}
              sx={{
                color: leave.hexColor,
                textDecoration: "underline",
                textDecorationStyle: "dotted",
                textDecorationColor: leave.hexColor,
              }}
            >
              {leave.reasonName}
            </Typography>
          </Box>
        );
      }
      return null;
    });
  };

  const onClickApprove = (isApprove: boolean) => {
    handleClose();
    dispatch(
      setupDataRequest({ isApprove, id: data?.id, warning: data?.warning })
    );
    approveDialog.open();
  };

  const handleClose = () => {
    requestLeaveDialog.close();
    const newURL = pathname.replace(`/${id}`, "");
    navigate(newURL, { replace: true });
    dispatch(formReqLeaveReset());
    dispatch(resetFormLeaveLongTerm());
    dispatch(resetRequestLeaveDetail());
  };

  const renderNote = (status: IApproveStatus, note: string) => {
    if (status === ApproveStatusValue.PENDING || !note) return null;
    return renderBoxContent(
      status === ApproveStatusValue.REFUSE ? "Lý do từ chối" : "Ghi chú",
      note
    );
  };

  const handleViewDetailEarlyRegistration = (idDetail) => {
    const newURL = pathname.replace(
      `/${declareLeaveURL.leave}/${id}`,
      `/${declareLeaveURL.earlyRegistration}/${idDetail}`
    );
    navigate(newURL, { replace: true });

    dispatch(setEarlyRegistrationDetailId(idDetail));
    dispatch(setDeleteEarlyRegistrationId(idDetail));
    dispatch(getEarlyRegistrationDetailStart());
  };

  const isDisableAction = useMemo(() => {
    if (data?.currentApproveStatus !== ApproveStatusValue.PENDING) return true;
    if (
      data &&
      (data?.approvals ?? []).map((i) => i.approverCode).includes(employeeCode)
    )
      return false;
    return (
      data?.employeeCode === employeeCode ||
      (data?.relatedPersons ?? [])
        .map((item) => item.value)
        .includes(employeeCode)
    );
  }, [data, employeeCode]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitleCustom>
        <Title>Chi tiết đơn xin nghỉ</Title>
        <IconButton onClick={handleClose}>
          <IconClose />
        </IconButton>
      </DialogTitleCustom>
      {isString(data) && status !== ReduxStateType.INIT && (
        <DialogContent>
          <EmptyState message={data} />
        </DialogContent>
      )}
      {status === ReduxStateType.SUCCESS && (
        <>
          <DialogContent
            sx={(theme) => ({
              background: theme.palette.background.bgTertiary,
              p: 0,
            })}
          >
            <Box
              sx={(theme) => ({
                margin: 2,
                padding: pxToRem(20),
                background: "white",
                borderRadius: pxToRem(8),
              })}
            >
              {renderBoxContent(
                TitleContent.createDate,
                formatDateStringToDDMMYYYY(data?.registerDate)
              )}
              {renderBoxContent(
                TitleContent.employeeName,
                data?.employeeName,
                false,
                0
              )}
              {renderBoxContent(
                "",
                `${data?.positionName} - ${data?.departmentName}`
              )}
              <Box mt={0.5} mb={pxToRem(20)}>
                <Typography
                  sx={(theme) => ({
                    fontSize: theme.typography.size.sm_14,
                    color: theme.palette.text.textSecondary,
                    fontWeight: theme.typography.fw_regular_400,
                  })}
                  mb={1}
                >
                  Thông tin ngày nghỉ
                </Typography>
                <Box
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.stroke.strokeDefault}`,
                    borderRadius: pxToRem(8),
                    paddingX: 2,
                    paddingY: 0.5,
                  })}
                >
                  {renderLeaveDetails()}
                </Box>
              </Box>
              {renderBoxContent(
                TitleContent.totalLeave,
                `${data?.totalLeaveDay ?? 0} ngày`
              )}
              {renderBoxContent(TitleContent.leaveReason, data?.leaveReason)}
              {renderBoxContent(
                TitleContent.relatedPerson,
                (data?.relatedPersons ?? []).map((i) => i.label).join(", "),
                true
              )}

              {data?.approvals?.map((approval, index) => (
                <>
                  {renderBoxContent(
                    `${TitleContent.approval} (${index + 1})`,
                    `${approval.approverName} - ${ApproveStatus[approval.approveStatus]}`
                  )}
                  {renderNote(approval.approveStatus, approval.refuseReason)}
                </>
              ))}
              {renderBoxContent(
                TitleContent.status,
                data?.finalApproveStatus,
                false,
                1.5,
                600
              )}

              {renderBoxContent(
                TitleContent.approvalDate,
                data?.finalApproveDate
                  ? formatDateStringToDDMMYYYY(data?.finalApproveDate)
                  : "---"
              )}

              {(data?.attachedFiles ?? []).length > 0 && (
                <Box mb={1}>
                  <Typography
                    sx={(theme) => ({
                      fontSize: theme.typography.size.sm_14,
                      color: theme.palette.text.textSecondary,
                      fontWeight: theme.typography.fw_regular_400,
                    })}
                  >
                    File đính kèm
                  </Typography>
                  <Box display={"flex"} maxWidth={"inherit"} overflow={"auto"}>
                    {(data?.attachedFiles ?? []).map((document, index) => (
                      <ListDocument key={index} content={document} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
            <Box display="flex" width={"100%"}>
              <Button
                variant="blur"
                fullWidth
                sx={(theme) => ({
                  mr: 1,
                  background: theme.palette.background.bgSecondary,
                  color: theme.palette.text.textPrimary,
                })}
                onClick={handleClose}
              >
                Đóng
              </Button>
              {data?.registerEarlyId && (
                <Button
                  variant="blur"
                  color="primary"
                  fullWidth
                  onClick={() => {
                    if (data?.registerEarlyId) {
                      handleViewDetailEarlyRegistration(data?.registerEarlyId);
                    } else {
                      const newURL = pathname.replace(`/${id}`, "");
                      navigate(newURL, { replace: true });
                    }
                    earlyRegistrationDialog.open();
                    requestLeaveDialog.close();
                  }}
                  sx={(theme) => ({
                    fontSize: pxToRem(14),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: pxToRem(1),
                    mr: 1,
                  })}
                >
                  <IconCalendarEdit />
                  <span>Đi làm sớm</span>
                </Button>
              )}
              <Button
                color="success"
                fullWidth
                sx={{ mr: 1, gap: 1 }}
                disabled={isDisableAction}
                onClick={() => onClickApprove(true)}
              >
                Phê duyệt
              </Button>
              <Button
                color="error"
                fullWidth
                sx={{ gap: 1 }}
                disabled={isDisableAction}
                onClick={() => onClickApprove(false)}
              >
                Từ chối
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default LeaveRequestDetailManager;
